import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { FadeInUp } from '../components/animations';
import SEO from '../components/SEO';
import { breadcrumbSchema } from '../utils/schema';

const HowItWorksPage = () => {
  const steps = [
    {
      num: '01',
      title: 'Requirement',
      description: 'The buyer shares product specifications, quantity, quality expectations, target pricing and delivery timeline. This helps us understand what needs to be sourced.',
    },
    {
      num: '02',
      title: 'Manufacturer Identification',
      description: 'Based on the requirement, we identify suitable Indian manufacturers with the right capabilities, capacity and product expertise.',
    },
    {
      num: '03',
      title: 'Quotation & Comparison',
      description: 'We coordinate quotations from shortlisted manufacturers and present a structured comparison to help the buyer make an informed decision.',
    },
    {
      num: '04',
      title: 'Sample / Approval',
      description: 'Once a manufacturer is selected, we coordinate the sample development process and manage communication until the buyer approves the sample.',
    },
    {
      num: '05',
      title: 'Production Coordination',
      description: 'During production, we follow up with the manufacturer on timeline, quality and any requirements. Regular updates are shared with the buyer.',
    },
    {
      num: '06',
      title: 'Quality Check',
      description: 'We coordinate quality inspections at key production stages to ensure the final product meets the agreed specifications and standards.',
    },
    {
      num: '07',
      title: 'Packing & Shipment',
      description: 'We coordinate packaging as per buyer requirements and manage the shipment documentation and logistics to the destination country.',
    },
  ];

  return (
    <div className="pt-20" data-testid="how-it-works-page">
      <SEO
        title="How It Works | Sourcing Process | Taiga Sourcing"
        description="From requirement to shipment — our 7-step sourcing process. Manufacturer identification, quotation, sampling, production, quality check and shipment coordination."
        keywords="India sourcing process, how sourcing works India, sourcing steps, requirement to shipment"
        path="/how-it-works"
        schema={breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'How It Works', path: '/how-it-works' }])}
      />
      <section className="relative py-24 lg:py-32 bg-[#071E45] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <FadeInUp>
            <p className="text-sm tracking-[0.3em] uppercase text-white/50 mb-4 font-medium">Our Process</p>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-[1.1] mb-6" style={{ fontFamily: 'Cabinet Grotesk' }} data-testid="how-it-works-title">
              FROM REQUIREMENT TO SHIPMENT.
            </h1>
            <p className="text-base text-white/70 leading-relaxed max-w-3xl mx-auto">
              A structured sourcing process that takes your product requirement and coordinates everything through to final shipment.
            </p>
          </FadeInUp>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="space-y-0">
            {steps.map((step, i) => (
              <FadeInUp key={i} delay={i * 0.05}>
                <div className="relative flex items-start gap-8 py-10 border-b border-[#E5E0D8]/60 last:border-b-0" data-testid={`how-step-${i}`}>
                  <div className="flex-shrink-0 w-16 text-center">
                    <span className="text-4xl font-bold text-[#0B2A57]/10" style={{ fontFamily: 'Cabinet Grotesk' }}>{step.num}</span>
                  </div>
                  {i < steps.length - 1 && (
                    <div className="absolute left-8 top-[4.5rem] bottom-0 w-px bg-[#E5E0D8]" />
                  )}
                  <div>
                    <h3 className="text-base font-bold tracking-wide uppercase text-[#0B2A57] mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>{step.title}</h3>
                    <p className="text-sm text-[#6B7280] leading-relaxed">{step.description}</p>
                  </div>
                </div>
              </FadeInUp>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-[#071E45] text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <FadeInUp>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Cabinet Grotesk' }}>
              START YOUR SOURCING JOURNEY.
            </h2>
            <p className="text-base text-white/60 leading-relaxed mb-8">
              Share your requirement and let us coordinate the sourcing process in India.
            </p>
            <Link
              to="/request-sourcing"
              className="inline-flex items-center gap-2 px-10 py-4 bg-white text-[#0B2A57] text-sm font-semibold tracking-wider uppercase transition-all hover:shadow-xl hover:-translate-y-0.5"
              data-testid="how-cta"
            >
              Request Sourcing <ArrowRight size={18} />
            </Link>
          </FadeInUp>
        </div>
      </section>
    </div>
  );
};

export default HowItWorksPage;
